import { Calendar } from "@root/shared/components/Calendar";
import { ChoiceChip, type Choice } from "@root/shared/components/ChoiceChip";
import { RadioButton } from "@root/shared/components/RadioButton";
import { TextButton } from "@root/shared/components/TextButton";
import { type FC } from "react";
import { InformationText } from "@root/shared/components/InformationText";
import { type DateRange } from "@userFrontend/features/entry/types/preferredDayAndTimes";
import * as styles from "./styles.css";

type TimeOption = {
  id: string;
  label: string;
  value: string;
};

type Props = {
  candidateNumber: number;
  dateRange: DateRange;
  dayFieldName: string;
  timesFieldName: string;
  selectedDate: Date | undefined;
  selectedTimeOption: string | undefined;
  selectedTimeSlots: number[];
  timeOptions: TimeOption[];
  timeSlotChoices: Choice<number>[];
  allDayValue: number;
  showTimeSlots: boolean;
  hasError: boolean;
  dayErrorText?: string;
  timesErrorText?: string;
  buttonText: string;
  onDateChange: (date: Date | undefined) => void;
  onTimeOptionChange: (value: string) => void;
  onTimeSlotsChange: (slots: number[]) => void;
  onButtonClick: () => void;
};

export const PreferredDayAndTimeItem: FC<Props> = ({
  candidateNumber,
  dateRange,
  dayFieldName,
  timesFieldName,
  selectedDate,
  selectedTimeOption,
  selectedTimeSlots,
  timeOptions,
  timeSlotChoices,
  allDayValue,
  showTimeSlots,
  hasError,
  dayErrorText,
  timesErrorText,
  buttonText,
  onDateChange,
  onTimeOptionChange,
  onTimeSlotsChange,
  onButtonClick,
}) => {
  return (
    <div className={styles.itemWrapper}>
      <div className={styles.titleWrapper}>
        <p className={styles.title}>第{candidateNumber}候補</p>
        <TextButton size="large" onClick={onButtonClick}>
          {buttonText}
        </TextButton>
      </div>
      <div className={styles.calendarWrapper}>
        <Calendar
          selectedDate={selectedDate}
          handleOnSelect={onDateChange}
          startDate={new Date(dateRange.startDate)}
          endDate={new Date(dateRange.endDate)}
          name={dayFieldName}
          hasError={hasError}
          placeholder="0000年00月00日"
        />
        {hasError && (
          <div className={styles.errorTextWrapper}>
            {dayErrorText && <InformationText isAlert text={dayErrorText} />}
            {timesErrorText && <InformationText isAlert text={timesErrorText} />}
          </div>
        )}
      </div>
      <div className={styles.timeOptions}>
        {timeOptions.map((opt) => (
          <label key={opt.id} htmlFor={opt.id} className={styles.label}>
            <RadioButton
              id={opt.id}
              name={`user-hoped-interview-time-option-${candidateNumber}`}
              value={opt.value}
              checked={selectedTimeOption === opt.value}
              onChange={() => onTimeOptionChange(opt.value)}
            />
            {opt.label}
          </label>
        ))}
      </div>
      {showTimeSlots && (
        <div className={styles.choiceChipWrapper}>
          <ChoiceChip
            values={selectedTimeSlots}
            onChange={onTimeSlotsChange}
            choices={timeSlotChoices}
          />
          {selectedTimeSlots.map((slot) => (
            <input
              key={`hidden-time-${candidateNumber}-${slot}`}
              type="hidden"
              name={`${timesFieldName}[]`}
              value={String(slot)}
            />
          ))}
        </div>
      )}
      {selectedTimeOption === "allDay" && (
        <input type="hidden" name={`${timesFieldName}[]`} value={String(allDayValue)} />
      )}
    </div>
  );
};
